import { useState } from "react";
import { List } from "../../../types";

export type ListMoveConfirmKind = "into-shared" | "owner-moving-out";

export interface PendingListMove {
  kind: ListMoveConfirmKind;
  targetList: List | null; // null means "no list" (clearing)
  targetLabel: string; // list name, or "No list"
}

interface UseListMoveConfirmArgs {
  /** Only edit mode can "move" a Task — a brand-new one has nowhere to move
   * from. */
  mode: "add" | "edit";
  currentListId: string | null | undefined;
  viewerId: string | undefined;
  lists: List[];
  sharedListIds: Set<string>;
  onCommitSelect: (list: List) => void;
  onCommitClear: () => void;
}

/**
 * The two confirmations item 6 calls for: moving a Task into a Shared list
 * (applies whoever does it — everyone in that list will now see it), and
 * the Owner moving one out (Owner-specific: a Member moving their own Task
 * back out is the unremarkable case ADR-0003 already lets them do freely).
 * Wraps ListPicker's onSelectList/onClearList so TaskDialog.tsx doesn't
 * grow — it's pinned at its grandfathered line ceiling.
 */
export function useListMoveConfirm({
  mode,
  currentListId,
  viewerId,
  lists,
  sharedListIds,
  onCommitSelect,
  onCommitClear,
}: UseListMoveConfirmArgs) {
  const [pending, setPending] = useState<PendingListMove | null>(null);

  const currentIsOwnedByViewer =
    !!currentListId &&
    lists.find((l) => l.id === currentListId)?.user_id === viewerId;

  function decide(targetList: List | null): ListMoveConfirmKind | null {
    if (mode !== "edit") return null;
    const targetId = targetList?.id ?? null;
    if (targetId === (currentListId ?? null)) return null;
    if (targetId && sharedListIds.has(targetId)) return "into-shared";
    if (currentListId && sharedListIds.has(currentListId) && currentIsOwnedByViewer) {
      return "owner-moving-out";
    }
    return null;
  }

  const onSelectList = (list: List) => {
    const kind = decide(list);
    if (kind) {
      setPending({ kind, targetList: list, targetLabel: `${list.emoji} ${list.name}` });
      return;
    }
    onCommitSelect(list);
  };

  const onClearList = () => {
    const kind = decide(null);
    if (kind) {
      setPending({ kind, targetList: null, targetLabel: "No list" });
      return;
    }
    onCommitClear();
  };

  const confirm = () => {
    if (!pending) return;
    if (pending.targetList) onCommitSelect(pending.targetList);
    else onCommitClear();
    setPending(null);
  };

  const cancel = () => setPending(null);

  return { onSelectList, onClearList, pending, confirm, cancel };
}
